/**
 * ThisWeekPanel — the Autopilot "This Week" cockpit (bold bet #2 surface).
 *
 * Renders the ranked cross-account ops queue assembled by the PURE engine
 * (`lib/crm/weeklyOps.ts`): every at-risk account, overdue follow-up,
 * overdue deliverable, high scan finding, and weekly update due — grouped
 * by kind, each with its honest reason line and transparent priority score.
 * The panel is a THIN renderer: it computes nothing, fabricates nothing,
 * and shows a straight empty state when the engine returns no work.
 */

import {
  AlertTriangle,
  BellRing,
  CalendarRange,
  Flame,
  Mail,
  PackageX,
  ShieldCheck,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import { weeklyOpsCounts, type WeeklyOpsItem, type WeeklyOpsKind } from '../lib/crm/weeklyOps';

/** Fixed group order — most pressing kind first, mirroring the score bases. */
const KIND_ORDER: WeeklyOpsKind[] = [
  'at_risk',
  'stale_finding',
  'overdue_deliverable',
  'overdue_followup',
  'weekly_update',
];

const KIND_META: Record<
  WeeklyOpsKind,
  { label: string; icon: LucideIcon; color: string; pill: string }
> = {
  at_risk: {
    label: 'At-risk accounts',
    icon: AlertTriangle,
    color: C.magenta,
    pill: 'glass-pill glass-pill--magenta',
  },
  stale_finding: {
    label: 'High findings',
    icon: Flame,
    color: C.orange,
    pill: 'glass-pill glass-pill--orange',
  },
  overdue_deliverable: {
    label: 'Overdue deliverables',
    icon: PackageX,
    color: C.orange,
    pill: 'glass-pill glass-pill--orange',
  },
  overdue_followup: {
    label: 'Overdue follow-ups',
    icon: BellRing,
    color: C.blueElectric,
    pill: 'glass-pill glass-pill--blue',
  },
  weekly_update: {
    label: 'Weekly updates due',
    icon: Mail,
    color: C.blueElectric,
    pill: 'glass-pill glass-pill--blue',
  },
};

export function ThisWeekPanel({ items }: { items: WeeklyOpsItem[] }) {
  const counts = weeklyOpsCounts(items);

  return (
    <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
      <div className="dash-section-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            aria-hidden="true"
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              display: 'inline-grid',
              placeItems: 'center',
              background: items.length
                ? 'linear-gradient(135deg, rgba(0,130,243,0.22), rgba(255,0,255,0.10))'
                : 'linear-gradient(135deg, rgba(0,200,120,0.20), rgba(0,130,243,0.10))',
              border: '1px solid rgba(255,255,255,0.14)',
              color: items.length ? C.blueElectric : C.green,
            }}
          >
            <CalendarRange size={17} strokeWidth={2} />
          </span>
          <div>
            <span className="t-eyebrow">Autopilot</span>
            <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
              This Week
            </h2>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            className="glass-pill"
            title="Every item is drawn from a live record signal and ranked by a transparent priority score — nothing invented."
          >
            {items.length} item{items.length === 1 ? '' : 's'} queued
          </span>
        </div>
      </div>

      <p className="t-meta" style={{ margin: '0 0 10px' }}>
        Your whole week across every account, pre-assembled into one ranked queue — at-risk
        accounts, fires from the system scan, overdue deliverables and follow-ups, and the weekly
        updates owed. Each line says why it&apos;s here.
      </p>

      {items.length === 0 ? (
        <div className="crm-empty" style={{ padding: '20px 0' }}>
          <ShieldCheck size={20} strokeWidth={1.75} />
          <span>
            Nothing queued — no at-risk accounts, no overdue work, and no updates due from the
            current records.
          </span>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          {KIND_ORDER.filter((kind) => counts[kind] > 0).map((kind) => {
            const meta = KIND_META[kind];
            const Icon = meta.icon;
            const group = items.filter((i) => i.kind === kind);
            return (
              <div key={kind}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    margin: '0 0 6px',
                  }}
                >
                  <Icon size={13} color={meta.color} aria-hidden="true" />
                  <span className="t-eyebrow">{meta.label}</span>
                  <span className={meta.pill} style={{ fontSize: 10 }}>
                    {group.length}
                  </span>
                </div>
                <ul
                  style={{
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    display: 'grid',
                    gap: 6,
                  }}
                >
                  {group.map((item) => (
                    <li
                      key={item.id}
                      className="glass-row"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '8px 12px',
                      }}
                    >
                      <span
                        aria-hidden="true"
                        title={`Priority ${item.priorityScore} — kind base plus a bounded magnitude (days overdue, health deficit, blockers)`}
                        style={{
                          flex: '0 0 auto',
                          width: 34,
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                          fontWeight: 700,
                          color: meta.color,
                        }}
                      >
                        {item.priorityScore}
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          className="t-card-title"
                          style={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.title}
                        </span>
                        <span className="t-meta" style={{ display: 'block', marginTop: 2 }}>
                          {item.reason}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </GlassPanel>
  );
}
